/**
 * Loads the reCAPTCHA API script on demand and resolves with the global grecaptcha object.
 *
 * reCAPTCHA calls the function named in its `onload` query parameter the moment the script
 * executes, so that function has to exist first. This defines it before creating the script
 * element, which makes the ordering causal rather than a race. A server-rendered script tag
 * cannot offer that guarantee - it competes with React's mount and loses whenever the browser
 * already has api.js cached, which is what broke the captcha on a form post.
 */
export const CALLBACK_NAME = '__ulRecaptchaLoaded';

const SCRIPT_SRC = `https://www.google.com/recaptcha/api.js?onload=${CALLBACK_NAME}&render=explicit`;

let loadPromise;

/**
 * Reuses the CSP nonce the server stamped onto its own script tags, so the injected tag is
 * allowed when script-src is tightened to 'strict-dynamic'.
 * @returns {string | undefined}
 */
function getCspNonce() {
    return Array.from(document.querySelectorAll('script[nonce]'))
        .map(script => script.nonce)
        .find(Boolean);
}

/**
 * @returns {Promise<object>} the global grecaptcha object, once the API is ready
 */
export default function loadRecaptcha() {
    if (!loadPromise) {
        loadPromise = new Promise((resolve, reject) => {
            window[CALLBACK_NAME] = () => {
                delete window[CALLBACK_NAME];
                resolve(window.grecaptcha);
            };

            const script = document.createElement('script');
            script.src = SCRIPT_SRC;
            script.async = true;

            const nonce = getCspNonce();
            if (nonce) {
                script.nonce = nonce;
            }

            script.onerror = () => {
                delete window[CALLBACK_NAME];
                // Allow a later mount to try again rather than caching the failure forever.
                loadPromise = undefined;
                reject(new Error('Unable to load reCAPTCHA.'));
            };

            document.head.appendChild(script);
        });
    }

    return loadPromise;
}
